import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId] = useState(searchParams.get('session_id'));
  const [loading, setLoading] = useState(true);
  const [sessionDetails, setSessionDetails] = useState(null);

  useEffect(() => {
    if (sessionId) {
      verifySession();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const verifySession = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_GATEWAY_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/stripe-verify-session/${sessionId}`);
      if (response.ok) {
        const details = await response.json();
        setSessionDetails(details);
        toast.success('Subscription activated successfully!');
      }
    } catch (error) {
      console.error('Error verifying session:', error);
      toast.error('Unable to verify payment. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Welcome to Chartara Notes!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your subscription has been activated successfully.
          </p>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            What happens next?
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <Clock className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Login credentials arriving shortly
                </p>
                <p className="text-sm text-gray-600">
                  You'll receive your login credentials via email shortly. 
                  If you don't receive them, please email{' '}
                  <a 
                    href="mailto:chartaralabs@gmail.com" 
                    className="text-blue-600 hover:text-blue-800"
                  >
                    chartaralabs@gmail.com
                  </a>
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <ArrowRight className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  First login setup
                </p>
                <p className="text-sm text-gray-600">
                  During your first login, you'll be prompted to create a new password 
                  for security. This ensures your account is fully secure and personalized.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Mail className="w-5 h-5 text-blue-500 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Check your email regularly
                </p>
                <p className="text-sm text-gray-600">
                  We'll send important updates about your subscription and new features 
                  to keep you informed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {sessionDetails && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Subscription Details
            </h4>
            <div className="text-sm text-blue-800">
              <p>Plan: {sessionDetails.planName}</p>
              <p>Amount: ${sessionDetails.amount}</p>
              <p>Next billing: {sessionDetails.nextBilling}</p>
            </div>
          </div>
        )}

        <div className="text-center space-y-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Continue to Dashboard
          </button>
          
          <p className="text-xs text-gray-500">
            Need help? Contact us at{' '}
            <a 
              href="mailto:chartaralabs@gmail.com" 
              className="text-blue-600 hover:text-blue-800"
            >
              chartaralabs@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;